import * as THREE from 'three';

export interface SpawnConfig {
  outerRadius: number;
  innerRadius: number;
  sectorAngleRange: number; // Angle range for spawn distribution
  minDistanceFromOthers: number;
}

export class SpawnPositioning {
  private static readonly DEFAULT_CONFIG: SpawnConfig = {
    outerRadius: 130, // Far from the solar system center
    innerRadius: 90, // Minimum spawn distance  
    sectorAngleRange: Math.PI * 2, // Full circle
    minDistanceFromOthers: 5,
  };

  /**
   * Generate a spawn position at the outer edge of the solar system
   * Ships will appear here and travel toward the mempool nebula
   */
  public static generateOuterEdgePosition(config: Partial<SpawnConfig> = {}): THREE.Vector3 {
    const finalConfig = { ...this.DEFAULT_CONFIG, ...config };

    // Generate random position on the outer sphere
    const theta = Math.random() * finalConfig.sectorAngleRange; // Azimuthal angle
    const phi = Math.acos(2 * Math.random() - 1); // Polar angle (uniform distribution on sphere)
    const radius = finalConfig.innerRadius + Math.random() * (finalConfig.outerRadius - finalConfig.innerRadius);

    // Convert spherical to Cartesian coordinates
    const x = radius * Math.sin(phi) * Math.cos(theta);
    const y = radius * Math.cos(phi) * 0.5; // Flatten Y to create more of a disk
    const z = radius * Math.sin(phi) * Math.sin(theta);

    return new THREE.Vector3(x, y, z);
  }

  /**
   * Generate mempool target position for Phase 1
   * Ships will travel from outer edge to this position
   */
  public static getMempoolTargetPosition(feeRate: number): THREE.Vector3 {
    // Mempool nebula center is at origin, but we can vary target based on fee rate
    const baseRadius = 25; // Mempool nebula radius
    const feeInfluence = Math.min(feeRate / 50, 1.0); // Normalize fee rate (0-1)
    
    // Higher fee ships target inner nebula, lower fee ships target outer nebula
    const targetRadius = baseRadius * (1 - feeInfluence * 0.6); // 40% to 100% of nebula radius
    
    // Random position within the target ring
    const angle = Math.random() * Math.PI * 2;
    const radiusVariation = targetRadius * (0.8 + Math.random() * 0.4); // ±20% variation
    
    const x = Math.cos(angle) * radiusVariation;
    const z = Math.sin(angle) * radiusVariation;
    const y = (Math.random() - 0.5) * 8; // Small Y variation
    
    return new THREE.Vector3(x, y, z);
  }

  /**
   * Calculate trajectory path from spawn to mempool
   * Uses curve for more natural movement
   */
  public static createTrajectoryPath(
    origin: THREE.Vector3,
    destination: THREE.Vector3,
    shipClass: 'micro' | 'standard' | 'priority' | 'whale'
  ): THREE.CatmullRomCurve3 {
    // Create intermediate control points for curved trajectory
    const midPoint1 = new THREE.Vector3().lerpVectors(origin, destination, 0.3);
    const midPoint2 = new THREE.Vector3().lerpVectors(origin, destination, 0.7);
    
    // Add slight curvature based on ship class
    const curvatureStrength = this.getCurvatureForShipClass(shipClass);
    const perpendicular = new THREE.Vector3()
      .crossVectors(
        new THREE.Vector3().subVectors(destination, origin).normalize(),
        new THREE.Vector3(0, 1, 0)
      )
      .normalize();
    
    // Apply curvature to middle points
    midPoint1.add(perpendicular.clone().multiplyScalar(curvatureStrength * 15));
    midPoint2.add(perpendicular.clone().multiplyScalar(-curvatureStrength * 10));
    
    // Create smooth curve
    return new THREE.CatmullRomCurve3([
      origin.clone(),
      midPoint1,
      midPoint2,
      destination.clone()
    ]);
  }

  /**
   * Determine ship class based on fee rate
   * Enhanced version for Phase 1
   */
  public static classifyShipByFeeRate(feeRate: number): 'micro' | 'standard' | 'priority' | 'whale' {
    // Updated thresholds based on current Bitcoin fee market
    if (feeRate >= 100) return 'whale';
    if (feeRate >= 50) return 'priority';
    if (feeRate >= 10) return 'standard';
    return 'micro';
  }

  /**
   * Get engine glow intensity based on fee rate
   * Higher fees = more urgent = brighter glow
   */
  public static getEngineGlowIntensity(feeRate: number): number {
    const normalized = Math.min(feeRate / 100, 1.0); // Normalize to 0-1
    return 0.5 + normalized * 1.5; // Range: 0.5 to 2.0
  }

  private static getCurvatureForShipClass(shipClass: 'micro' | 'standard' | 'priority' | 'whale'): number {
    const curvatureMap = {
      micro: 0.8,     // More curved, leisurely path
      standard: 0.5,  // Moderate curve
      priority: 0.3,  // Straighter, more direct
      whale: 0.1      // Nearly direct path
    };
    return curvatureMap[shipClass];
  }

  /**
   * Validate that a position is safe and reasonable
   */
  public static isValidPosition(position: THREE.Vector3 | {x: number, y: number, z: number}): boolean {
    const { x, y, z } = position;
    
    // Check for NaN or Infinity
    if (!isFinite(x) || !isFinite(y) || !isFinite(z)) {
      return false;
    }
    
    // Check for extremely large values (outside reasonable solar system bounds)
    const magnitude = Math.sqrt(x * x + y * y + z * z);
    if (magnitude > 1000 || magnitude < 0.1) {
      return false;
    }
    
    return true;
  }
}